// src/context/ListingsContext.js
// Gestion des annonces et favoris connectée à l'API PHP - NE PAS MODIFIER

import React, { createContext, useState, useCallback, useEffect } from 'react';
import apiClient from '../api/client';

export const ListingsContext = createContext();

const FAVORITES_KEY = 'fynho_favorites_guest'; // favoris pour visiteurs non connectés

export const ListingsProvider = ({ children }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState([]);

  // Charge les favoris invité depuis le localStorage au démarrage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      if (raw) setFavoriteIds(JSON.parse(raw));
    } catch {
      setFavoriteIds([]);
    }
  }, []);

  const fetchListings = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'list', ...filters }).toString();
      const result = await apiClient.get(`/endpoints/listings.php?${params}`);
      setListings(result.listings || []);
      return result.listings || [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNearby = useCallback(async (lat, lng, radius = 15, category = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: 'nearby',
        lat,
        lng,
        radius,
        ...(category ? { category } : {}),
      }).toString();
      const result = await apiClient.get(`/endpoints/listings.php?${params}`);
      return result.listings || [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchListingBySlug = useCallback(async (slug) => {
    const result = await apiClient.get(`/endpoints/listings.php?action=detail&slug=${encodeURIComponent(slug)}`);
    return result;
  }, []);

  const isFavorite = useCallback((listingId) => favoriteIds.includes(listingId), [favoriteIds]);

  const getFavorites = useCallback(() => favoriteIds, [favoriteIds]);

  const addToFavorites = useCallback((listingId) => {
    setFavoriteIds((prev) => {
      if (prev.includes(listingId)) return prev;
      const updated = [...prev, listingId];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
      return updated;
    });
    // Synchronise avec le serveur si l'utilisateur est connecté (échoue silencieusement sinon)
    apiClient.post('/endpoints/favorites.php?action=add', { listingId }).catch(() => {});
  }, []);

  const removeFromFavorites = useCallback((listingId) => {
    setFavoriteIds((prev) => {
      const updated = prev.filter((id) => id !== listingId);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
      return updated;
    });
    apiClient.delete(`/endpoints/favorites.php?action=remove&listingId=${listingId}`).catch(() => {});
  }, []);

  const value = {
    listings,
    loading,
    fetchListings,
    fetchNearby,
    fetchListingBySlug,
    isFavorite,
    getFavorites,
    addToFavorites,
    removeFromFavorites,
  };

  return <ListingsContext.Provider value={value}>{children}</ListingsContext.Provider>;
};